import ClientUtils from "./../../../utils/ClientUtils";
import { useRef } from "react";
import {
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";

import { UUID, Utils } from "@eljouren/utils";

interface Props<T extends FieldValues> {
  name: Path<T>;
  label?: string;
  errorMessage?: string;
  errors?: FieldErrors<T>;
  className?: string;
  helperText?: string;
  register: UseFormRegister<T>;
  options?: RegisterOptions<T>;
  htmlAttributes?: Omit<JSX.IntrinsicElements["input"], "ref">;
  data?: Record<string, string>;
  labelClassName?: string;
  inputClassName?: string;
}
export function AppFormTextField<T extends FieldValues>(props: Props<T>) {
  const id = useRef(UUID.generate().value).current;

  const options = props.options || {};
  if (!options.setValueAs && props.htmlAttributes?.type === "number") {
    options.setValueAs = (v) => (v === "" ? undefined : parseFloat(v));
  }

  const getError = () => {
    if (props.errorMessage) {
      return props.errorMessage;
    } else if (props.errors) {
      const errors = props.errors as any;
      if (props.name in errors) {
        return errors[props.name]?.message;
      }
    }

    return null;
  };
  const error = getError();

  let srOnlyLabel: string | undefined;
  if (!props.label && props.htmlAttributes?.placeholder) {
    srOnlyLabel = props.htmlAttributes.placeholder;
  }

  const data = Utils.objectKeyMap(props.data ?? {}, (key) => {
    return `data-${key}`;
  });

  return (
    <p
      className={ClientUtils.twClassNames(
        "flex w-full flex-col gap-1",
        props.className
      )}
    >
      {props.label && (
        <label
          htmlFor={id}
          className={ClientUtils.twClassNames("text-sm", props.labelClassName)}
        >
          {props.label}
        </label>
      )}
      {srOnlyLabel && (
        <label htmlFor={id} className="sr-only">
          {srOnlyLabel}
        </label>
      )}
      <input
        className={ClientUtils.twClassNames(
          "disabled:opacity-1 opacity-1 flex w-full rounded border fill-black p-2 text-black placeholder:text-sm placeholder:italic placeholder:text-black/60",
          props.htmlAttributes?.readOnly
            ? "border-form-border-disabled"
            : "border-form-border",
          props.inputClassName
        )}
        id={id}
        {...props.register(props.name, options)}
        {...props.htmlAttributes}
        {...data}
      ></input>
      {props.helperText && (
        <span className="pb-2 text-xs italic text-dark-gray text-opacity-80">
          {props.helperText}
        </span>
      )}
      {error && <span className="text-sm text-red-600">{error}</span>}
    </p>
  );
}
